// API service for communicating with the backend
// Use relative path by default (respects current domain)
// Can override with REACT_APP_API_BASE_URL env var for development
const API_BASE = process.env.REACT_APP_API_BASE_URL || (() => {
  // If running on a non-localhost domain or different port, use relative path
  // This allows the frontend to work regardless of deployment URL
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return '/api';
  }
  return '/api';
})();

class APIService {

  handleUnauthorized(errData = null) {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTime');
      this.token = null;
      this.user = null;

      const code = errData?.code || 'UNAUTHORIZED';
      const message = errData?.error || 'Your account has been deactivated or your session has expired. Please contact support.';

      window.dispatchEvent(new CustomEvent('auth:force-logout', {
        detail: { code, message }
      }));
    }
  }

  async get(endpoint) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'GET request failed');
    }
    return res.json();
  }

  async post(endpoint, body = {}) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body)
    });
    if (res.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const respBody = await res.json().catch(() => ({}));
      throw new Error(respBody.error || 'POST request failed');
    }
    return res.json();
  }

  async put(endpoint, body = {}) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body)
    });
    if (res.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const respBody = await res.json().catch(() => ({}));
      throw new Error(respBody.error || 'PUT request failed');
    }
    return res.json();
  }

  async updateUserLimits(username, customLimits) {
    return this.put(`/super-admin/users/${username}/limits`, { customLimits });
  }

  async patch(endpoint, body = {}) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body)
    });
    if (res.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const respBody = await res.json().catch(() => ({}));
      throw new Error(respBody.error || 'PATCH request failed');
    }
    return res.json();
  }

  async delete(endpoint, body = {}) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
    });
    if (res.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const respBody = await res.json().catch(() => ({}));
      throw new Error(respBody.error || 'DELETE request failed');
    }
    return res.json();
  }

  constructor() {
    this.user = null;
    this.token = null;
    this.activeUploads = {};
    this.loadUser();
  }

  loadUser() {
    // Load JWT token and user info from localStorage
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    this.token = token;
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (e) {
        this.user = null;
      }
    }
  }

  ensureTokenLoaded() {
    // Reload token before each API call to ensure it's current
    this.loadUser();
  }

  getAuthHeaders() {
    this.ensureTokenLoaded();
    return {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };
  }

  setSession(data) {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify({
      username: data.username,
      fullName: data.fullName || null,
      authProvider: data.authProvider || 'email',
      email: data.email,
      role: data.role,
      apiKey: data.apiKey,
      isOwner: data.isOwner,
      packageTier: data.packageTier,
      subscriptionStatus: data.subscriptionStatus || 'active',
      customLimits: data.customLimits || null,
      createdBy: data.createdBy || null,
      passwordSet: data.passwordSet !== undefined ? Boolean(data.passwordSet) : Boolean(JSON.parse(localStorage.getItem('user') || '{}').passwordSet),
      mustChangePassword: Boolean(data.mustChangePassword)
    }));
    this.token = data.token;
    this.user = {
      username: data.username,
      fullName: data.fullName || null,
      authProvider: data.authProvider || 'email',
      email: data.email,
      role: data.role,
      apiKey: data.apiKey,
      isOwner: data.isOwner,
      packageTier: data.packageTier,
      subscriptionStatus: data.subscriptionStatus || 'active',
      customLimits: data.customLimits || null,
      createdBy: data.createdBy || null,
      passwordSet: data.passwordSet !== undefined ? Boolean(data.passwordSet) : Boolean(JSON.parse(localStorage.getItem('user') || '{}').passwordSet),
      mustChangePassword: Boolean(data.mustChangePassword)
    };
    return this.user;
  }

  async login(username, password) {
    const res = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });

    if (res.status === 200) {
      const data = await res.json();
      return this.setSession(data);
    } else if (res.status === 401 || res.status === 403) {
      if (res.status === 401) this.logout();
      const body = await res.json().catch(() => ({}));
      if (res.status === 403 && body.requiresVerification) {
        const err = new Error(body.error || 'Email not verified');
        err.status = 403;
        err.requiresVerification = true;
        err.email = body.email;
        err.canSendOtp = body.canSendOtp !== false;
        throw err;
      }
      throw new Error(body.error || 'Invalid credentials');
    } else {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Login failed');
    }
  }

  async signup(username, password, plan = 'hobby', fullName = null) {
    const res = await fetch(`${API_BASE}/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, plan, fullName })
    });

    const data = await res.json().catch(() => ({}));
    if (res.status === 200 || res.status === 201) {
      if (data.requiresVerification) {
        return {
          requiresVerification: true,
          email: data.email || username,
          retryAfterSeconds: data.retryAfterSeconds || 60,
          expiresInSeconds: data.expiresInSeconds || 600,
          message: data.message,
          username: data.username
        };
      }
      return this.setSession(data);
    } else {
      const err = new Error(data.error || 'Signup failed');
      err.requiresVerification = data.requiresVerification;
      err.email = data.email;
      err.retryAfterSeconds = data.retryAfterSeconds;
      throw err;
    }
  }

  async verifyOtp(email, otp) {
    const res = await fetch(`${API_BASE}/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp })
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Verification failed');
    }

    this.setSession(data);
    return data;
  }

  async resendOtp(email) {
    const res = await fetch(`${API_BASE}/resend-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const data = await res.json().catch(() => ({}));
    if (res.status === 429) {
      const err = new Error(data.error || 'Please wait before requesting another code.');
      err.status = 429;
      err.retryAfterSeconds = data.retryAfterSeconds;
      throw err;
    }
    if (!res.ok) {
      throw new Error(data.error || 'Failed to resend code');
    }

    return data;
  }

  async forgotPassword(email) {
    const res = await fetch(`${API_BASE}/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Failed to request password reset code');
    }
    return data;
  }

  async verifyResetCode(email, code) {
    const res = await fetch(`${API_BASE}/verify-reset-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || 'Invalid or expired code');
      err.status = res.status;
      throw err;
    }
    return data;
  }

  async resetPassword(resetToken, newPassword) {
    const res = await fetch(`${API_BASE}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resetToken, newPassword })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Failed to reset password');
    }
    return data;
  }

  async verifyEmail(token) {
    const res = await fetch(`${API_BASE}/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Verification failed');
    return data;
  }

  async resendVerification(username) {
    const res = await fetch(`${API_BASE}/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to resend');
    return data;
  }

  async checkToken() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.token = null;
    this.user = null;
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.token = null;
    this.user = null;
  }

  async getStatus() {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/status`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch status');
    }

    return res.json();
  }

  async getAvailablePorts() {
    const res = await fetch(`${API_BASE}/status/ports/available`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch available ports');
    }

    return res.json();
  }

  async checkPortAvailability(port) {
    const res = await fetch(`${API_BASE}/status/ports/check?port=${port}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    return res.json();
  }

  async uploadApp(appName, file, options = {}) {
    const formData = new FormData();
    formData.append('appName', appName);
    if (options.domain) formData.append('domain', options.domain);
    if (options.publicPort) formData.append('publicPort', options.publicPort);
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    return new Promise((resolve, reject) => {
      xhr.open('POST', `${API_BASE}/upload`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      if (options.deploymentId) {
        xhr.setRequestHeader('x-deployment-id', options.deploymentId);
        this.activeUploads[options.deploymentId] = { type: 'xhr', req: xhr };
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          if (options.onUploadProgress) {
            options.onUploadProgress(percentComplete);
          }
        }
      };

      xhr.onload = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          let msg = 'Upload failed';
          try { msg = JSON.parse(xhr.responseText).error || msg; } catch (e) { }
          reject(new Error(msg));
        }
      };

      xhr.onerror = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        reject(new Error('Network error'));
      };
      
      xhr.onabort = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        reject(new Error('Upload aborted'));
      };

      xhr.send(formData);
    });
  }

  /**
   * Upload a ZIP into a backend AI workspace (same path as GitHub/GitLab backend deploy).
   * Opens as async deployment; poll getDeploymentStatus(deploymentId).
   */
  async deployBackendZip(appName, file, options = {}) {
    this.ensureTokenLoaded();
    const formData = new FormData();
    formData.append('appName', appName);
    formData.append('file', file);
    if (options.domain) formData.append('domain', options.domain);
    const publicPort = options.publicPort != null ? options.publicPort : options.port;
    if (publicPort != null && publicPort !== '') formData.append('publicPort', String(publicPort));
    if (options.image) formData.append('image', options.image);
    if (options.cpus != null) formData.append('cpus', String(options.cpus));
    if (options.memory) formData.append('memory', String(options.memory));
    if (options.env && typeof options.env === 'object') {
      formData.append('env', JSON.stringify(options.env));
    }
    if (options.entryPoints && Array.isArray(options.entryPoints) && options.entryPoints.length > 0) {
      formData.append('entryPoints', JSON.stringify(options.entryPoints));
    }

    const xhr = new XMLHttpRequest();
    return new Promise((resolve, reject) => {
      xhr.open('POST', `${API_BASE}/import-backend-zip`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      if (options.deploymentId) {
        xhr.setRequestHeader('x-deployment-id', options.deploymentId);
        this.activeUploads[options.deploymentId] = { type: 'xhr', req: xhr };
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && typeof options.onUploadProgress === 'function') {
          options.onUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        let body = {};
        try { body = JSON.parse(xhr.responseText || '{}'); } catch (_) { body = {}; }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(body);
        } else {
          reject(new Error(body.error || 'Backend ZIP workspace import failed'));
        }
      };

      xhr.onerror = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        reject(new Error('Network error'));
      };

      xhr.onabort = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        reject(new Error('Upload aborted'));
      };

      xhr.send(formData);
    });
  }

  async uploadDocker(appName, internalPort, category, file, cpus = 1.0, memory = '512m', options = {}) {
    const formData = new FormData();
    formData.append('appName', appName);
    formData.append('internalPort', internalPort);
    formData.append('category', category);
    if (options.domain) formData.append('domain', options.domain);
    if (options.publicPort) formData.append('publicPort', options.publicPort);
    formData.append('cpus', cpus);
    formData.append('memory', memory);
    // Add volumes as JSON
    if (options.volumes && Array.isArray(options.volumes)) {
      formData.append('volumes', JSON.stringify(options.volumes));
    }
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    return new Promise((resolve, reject) => {
      xhr.open('POST', `${API_BASE}/upload-docker-image`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      if (options.deploymentId) {
        xhr.setRequestHeader('x-deployment-id', options.deploymentId);
        this.activeUploads[options.deploymentId] = { type: 'xhr', req: xhr };
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          if (options.onUploadProgress) {
            options.onUploadProgress(percentComplete);
          }
        }
      };

      xhr.onload = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          let msg = 'Deployment failed';
          try { msg = JSON.parse(xhr.responseText).error || msg; } catch (e) { }
          reject(new Error(msg));
        }
      };

      xhr.onerror = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        reject(new Error('Network error'));
      };
      
      xhr.onabort = () => {
        if (options.deploymentId) delete this.activeUploads[options.deploymentId];
        reject(new Error('Upload aborted'));
      };

      xhr.send(formData);
    });
  }

  async deployDockerHub(appName, imageName, internalPort, category, cpus = 1.0, memory = '512m', options = {}) {
    const body = {
      appName,
      imageName,
      internalPort,
      category,
      cpus,
      memory,
      domain: options.domain,
      publicPort: options.publicPort,
      env: options.env || {},
      volumes: options.volumes || []
    };

    const res = await fetch(`${API_BASE}/deploy-docker-hub`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'x-deployment-id': options.deploymentId || ''
      },
      body: JSON.stringify(body)
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Docker Hub deployment failed');
    }

    return res.json();
  }

  async checkDeploymentQuota(options = {}) {
    this.ensureTokenLoaded();
    const query = new URLSearchParams();
    if (options.cpu) query.append('cpus', options.cpu);
    if (options.memory) query.append('memory', options.memory);
    
    const res = await fetch(`${API_BASE}/deployments/quota-check?${query.toString()}`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });
    if (!res.ok) {
      let err;
      try { err = await res.json(); } catch (e) { err = {}; }
      throw new Error(err.error || 'Quota check failed');
    }
    return res.json();
  }

  async deployComposeEnv(appName, domain = '', options = {}) {
    this.ensureTokenLoaded();
    // If a file is provided, send multipart/form-data so the backend can
    // extract the ZIP into the compose workspace.
    if (options && options.file) {
      const formData = new FormData();
      formData.append('appName', appName);
      if (domain) formData.append('domain', domain);
      if (options.cpu) formData.append('cpu', options.cpu);
      if (options.memory) formData.append('memory', options.memory);
      if (options.internalPort) formData.append('internalPort', options.internalPort);
      if (options.userPorts && Array.isArray(options.userPorts)) {
        options.userPorts.forEach(port => formData.append('userPorts[]', port));
      }
      if (options.entryPoints && Array.isArray(options.entryPoints) && options.entryPoints.length > 0) {
        formData.append('entryPoints', JSON.stringify(options.entryPoints));
      }
      formData.append('file', options.file);

      const res = await fetch(`${API_BASE}/deploy-compose-env`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'x-deployment-id': options.deploymentId || ''
        },
        body: formData
      });

      if (res.status === 401) throw new Error('Unauthorized');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to deploy Compose Environment');
      }
      return {
        deploymentId: res.headers.get('x-deployment-id'),
        ...(await res.json())
      };
    }

    // Fallback: JSON POST when no file is present
    const res = await fetch(`${API_BASE}/deploy-compose-env`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'x-deployment-id': options.deploymentId || ''
      },
      body: JSON.stringify({ appName, domain, options: { ...options, entryPoints: options.entryPoints } })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to deploy Compose Environment');
    }
    return {
      deploymentId: res.headers.get('x-deployment-id'),
      ...(await res.json())
    };
  }

  async getContainerFiles(appName, path = '/app', owner = null) {
    this.ensureTokenLoaded();
    let url = `${API_BASE}/terminal/${encodeURIComponent(appName)}/files?path=${encodeURIComponent(path)}`;
    if (owner) url += `&owner=${encodeURIComponent(owner)}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) throw new Error('Failed to list files');
    return res.json();
  }

  async extractContainerZip(appName, filename, path = '/app', owner = null) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/terminal/${encodeURIComponent(appName)}/files/extract`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ filename, path, owner })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) throw new Error('Failed to extract zip');
    return res.json();
  }

  async deployTemplate(appName, templateId, options = {}) {
    const body = {
      appName,
      templateId,
      domain: options.domain,
      publicPort: options.publicPort,
      internalPort: options.internalPort,
      volumes: options.volumes,
      userPorts: options.userPorts,
      dbUser: options.dbUser,
      dbPassword: options.dbPassword,
      dbName: options.dbName,
      entryPoints: options.entryPoints,
      cpus: options.cpus,
      memory: options.memory
    };

    const res = await fetch(`${API_BASE}/deploy-template`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'x-deployment-id': options.deploymentId || ''
      },
      body: JSON.stringify(body)
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to deploy template stack');
    }

    return res.json();
  }

  async autoDetectDeploy(appName, file, options = {}) {
    const formData = new FormData();
    formData.append('appName', appName);
    formData.append('file', file);
    if (options.domain) formData.append('domain', options.domain);
    if (options.publicPort) formData.append('publicPort', options.publicPort);
    if (options.internalPort) formData.append('internalPort', options.internalPort);
    if (options.dbUser) formData.append('dbUser', options.dbUser);
    if (options.dbPassword) formData.append('dbPassword', options.dbPassword);
    if (options.dbName) formData.append('dbName', options.dbName);

    const res = await fetch(`${API_BASE}/auto-deploy`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'x-deployment-id': options.deploymentId || ''
      },
      body: formData
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to auto-deploy stack');
    }

    return res.json();
  }


  async importFrontendRepo(appName, repoUrl, options = {}) {
    const payload = {
      appName,
      repoUrl,
      branch: options.branch,
      domain: options.domain,
      publicPort: options.publicPort,
      rootDirectory: options.rootDirectory,
      installCommand: options.installCommand,
      buildCommand: options.buildCommand,
      outputDir: options.outputDir,
      env: options.env,
      deployNow: options.deployNow,
      deploymentMode: options.deploymentMode,
      entryPoints: options.entryPoints,
      additionalFiles: options.additionalFiles
    };

    const res = await fetch(`${API_BASE}/import-frontend-repo`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'x-deployment-id': options.deploymentId || ''
      },
      body: JSON.stringify(payload)
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Repository deployment failed');
    }

    return {
      deploymentId: res.headers.get('x-deployment-id'),
      ...(await res.json())
    };
  }

  async importBackendRepo(appName, repoUrl, options = {}) {
    const payload = {
      appName,
      repoUrl,
      branch: options.branch,
      domain: options.domain,
      publicPort: options.port,
      rootDirectory: options.rootDirectory,
      image: options.image,
      env: options.env,
      cpus: options.cpus,
      memory: options.memory,
      deployNow: options.deployNow,
      userPorts: options.userPorts,
      entryPoints: options.entryPoints,
      additionalFiles: options.additionalFiles
    };

    const res = await fetch(`${API_BASE}/import-backend-repo`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'x-deployment-id': options.deploymentId || ''
      },
      body: JSON.stringify(payload)
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Repository deployment failed');
    }

    return {
      deploymentId: res.headers.get('x-deployment-id'),
      ...(await res.json())
    };
  }

  async scanGitEntries(repoUrl, branch = 'main') {
    const res = await fetch(`${API_BASE}/scan-git-entries`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ repoUrl, branch })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Repository scan failed');
    }
    return res.json();
  }

  async detectComposeConfig(provider, owner, repo, branch, rootDirectory = './', extra = {}) {
    const params = new URLSearchParams();
    params.append('owner', owner || '');
    params.append('repo', repo || '');
    params.append('branch', branch || 'main');
    params.append('rootDirectory', rootDirectory || './');
    if (extra && extra.fullName) params.append('fullName', extra.fullName);
    if (extra && extra.projectId) params.append('projectId', String(extra.projectId));
    const res = await fetch(`${API_BASE}/git/providers/${provider}/detect-compose?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to detect compose configuration');
    }
    return res.json();
  }

  async deployComposeRepo(appName, options = {}) {
    const env = {};
    if (Array.isArray(options.envVars)) {
      options.envVars.forEach(v => {
        if (v && v.key) env[String(v.key).trim()] = String(v.value);
      });
    }
    const res = await fetch(`${API_BASE}/import-compose-repo`, {
      method: 'POST',
      headers: Object.assign(this.getAuthHeaders(), {
        'Content-Type': 'application/json',
        'x-deployment-id': options.deploymentId || ''
      }),
      body: JSON.stringify({
        appName,
        repoUrl: options.repoUrl || '',
        branch: options.branch || 'main',
        rootDirectory: options.rootDir || './',
        entryService: options.entryService || 'web',
        internalPort: options.internalPort ? Number(options.internalPort) : 80,
        domain: options.domain || '',
        deploymentId: options.deploymentId || '',
        deployNow: true,
        cpu: options.cpu || options.cpus,
        memory: options.memory,
        env,
        additionalFiles: options.additionalFiles,
        userPorts: options.exposedPortsStr ? options.exposedPortsStr.split(',').map(p => Number(p.trim())).filter(p => !isNaN(p)) : []
      })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Compose deployment failed');
    }
    return {
      deploymentId: res.headers.get('x-deployment-id'),
      ...(await res.json().catch(() => ({})))
    };
  }

  async deployGitRepo(appName, options = {}) {
    // Convert envVars array to env object
    let envObj = {};
    if (Array.isArray(options.envVars)) {
      options.envVars.forEach(ev => {
        if (ev.key) envObj[ev.key] = ev.value || '';
      });
    } else if (options.envVars && typeof options.envVars === 'object') {
      envObj = options.envVars;
    }

    return this.importFrontendRepo(appName, options.repoUrl, {
      branch: options.branch,
      rootDirectory: options.rootDir,
      installCommand: options.installCommand,
      buildCommand: options.buildCommand,
      outputDir: options.outputDir,
      env: envObj,
      deploymentId: options.deploymentId,
      additionalFiles: options.additionalFiles,
      deployNow: true
    });
  }

  async deployBackendRepo(appName, options = {}) {
    let envObj = {};
    if (Array.isArray(options.envVars)) {
      options.envVars.forEach(ev => {
        if (ev.key) envObj[ev.key] = ev.value || '';
      });
    } else if (options.envVars && typeof options.envVars === 'object') {
      envObj = options.envVars;
    }

    return this.importBackendRepo(appName, options.repoUrl, {
      branch: options.branch,
      rootDirectory: options.rootDir,
      image: options.image,
      port: options.port,
      cpus: options.cpus,
      memory: options.memory,
      env: envObj,
      deploymentId: options.deploymentId,
      additionalFiles: options.additionalFiles,
      userPorts: options.exposedPortsStr ? options.exposedPortsStr.split(',').map(p => Number(p.trim())).filter(p => !isNaN(p)) : [],
      deployNow: true
    });
  }

  async getGitProviderStatus() {
    this.ensureTokenLoaded();
    if (!this.token) {
      // Return empty providers if not authenticated
      return { providers: { github: false, gitlab: false } };
    }
    const res = await fetch(`${API_BASE}/git/providers`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch Git provider status');
    }

    return res.json();
  }

  async getGitOAuthUrl(provider) {
    const res = await fetch(`${API_BASE}/git/oauth/${encodeURIComponent(provider)}/url`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to start ${provider} OAuth`);
    }

    return res.json();
  }

  async getGitOAuthAuthUrl(provider) {
    const res = await fetch(`${API_BASE}/git/oauth/${encodeURIComponent(provider)}/auth-url`, {
      method: 'GET'
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to start ${provider} OAuth authentication`);
    }

    return res.json();
  }

  async getAppLogs(appName, options = {}) {
    this.ensureTokenLoaded();
    const params = new URLSearchParams();
    if (options.owner) params.set('owner', options.owner);
    if (options.type) params.set('type', options.type);
    params.set('lines', String(options.lines || 200));
    const qs = `?${params.toString()}`;

    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/logs${qs}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch logs');
    }

    return res.json();
  }

  async getGitProvidersStatus() {
    const res = await fetch(`${API_BASE}/git/providers`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch Git provider status');
    }
    return res.json();
  }

  async connectGitProvider(provider, token) {
    // Ensure token is a non-empty string, otherwise use OAuth flow
    const effectiveToken = typeof token === 'string' ? token.trim() : '';
    
    if (!effectiveToken) {
      // Fetch OAuth URL if no token is provided
      const res = await fetch(`${API_BASE}/git/oauth/${encodeURIComponent(provider)}/url`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      if (res.status === 401) throw new Error('Unauthorized');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to initiate connection with ${provider}`);
      }
      return res.json();
    } else {
      // Connect using a PAT token
      const res = await fetch(`${API_BASE}/git/providers/${encodeURIComponent(provider)}/connect`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ token: effectiveToken })
      });
      if (res.status === 401) throw new Error('Unauthorized');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to connect ${provider}`);
      }
      return res.json();
    }
  }

  async getGithubSetupUrl() {
    const res = await fetch(`${API_BASE}/git/github/setup-url`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch GitHub setup URL');
    }
    return res.json();
  }

  async getGithubSetupStatus() {
    const res = await fetch(`${API_BASE}/git/github/setup-status`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch GitHub setup status');
    }
    return res.json();
  }

  async getGithubAppInstallUrl() {
    const res = await fetch(`${API_BASE}/git/github-app/install-url`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch GitHub App install URL');
    }
    return res.json();
  }

  async disconnectGitProvider(provider) {
    const res = await fetch(`${API_BASE}/git/providers/${encodeURIComponent(provider)}/connect`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to disconnect ${provider}`);
    }

    return res.json();
  }

  async detectRepoFramework(provider, owner, repo, branch = 'main', extra = {}) {
    this.ensureTokenLoaded();
    const params = new URLSearchParams({ owner: owner || '', repo: repo || '', branch: branch || 'main' });
    if (extra && extra.fullName) params.set('fullName', extra.fullName);
    if (extra && extra.projectId) params.set('projectId', String(extra.projectId));
    const url = `${API_BASE}/git/providers/${provider}/detect-framework?${params.toString()}`;
    const res = await fetch(url, { headers: this.getAuthHeaders() });
    // Soft-fail → workspace, not static (unknown must not become Deploy Static Build).
    if (!res.ok) return { framework: 'auto', isBackend: true, suggestedImage: 'alma-dev-workspace' };
    return await res.json();
  }

  async getGitRepos(provider, options = {}) {
    this.ensureTokenLoaded();
    if (!this.token) {
      throw new Error('Must be logged in to list repositories');
    }
    const params = new URLSearchParams();
    if (options.q) params.set('q', options.q);
    if (options.page) params.set('page', String(options.page));
    if (options.perPage) params.set('perPage', String(options.perPage));

    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`${API_BASE}/git/providers/${encodeURIComponent(provider)}/repos${qs}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      signal: options.signal
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to list ${provider} repositories`);
    }

    return res.json();
  }


  async getApp(appName, owner) {
    const ownerQuery = owner ? `?owner=${encodeURIComponent(owner)}` : '';
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}${ownerQuery}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) {
      throw new Error('Unauthorized');
    }
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to get app');
    return data;
  }

  async rebuildFrontend(appName, owner, options = {}) {
    const ownerQuery = owner ? `?owner=${encodeURIComponent(owner)}` : '';
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/rebuild${ownerQuery}`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'x-deployment-id': options.deploymentId || ''
      }
    });
    if (res.status === 401) {
      throw new Error('Unauthorized');
    }
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to rebuild frontend app');
    return data;
  }

  async updateAppConfig(appName, config, owner) {
    const body = { ...config };
    if (owner) body.owner = owner;
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/config`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body)
    });
    if (res.status === 401) {
      throw new Error('Unauthorized');
    }
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update app config');
    return data;
  }

  async deleteApp(appPath) {
    const parts = String(appPath || '').split('/').filter(Boolean);
    const appName = parts[parts.length - 1];
    const owner = parts.length >= 2 ? parts[parts.length - 2] : '';
    const ownerQuery = owner ? `?owner=${encodeURIComponent(owner)}` : '';
    
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}${ownerQuery}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || `Failed to delete app (${res.status})`);
    }
    return data;
  }

  async getAdminUsers(role) {
    const url = role ? `${API_BASE}/admin/users?role=${encodeURIComponent(role)}&includeDeleted=true` : `${API_BASE}/admin/users?includeDeleted=true`;
    const res = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || `Failed to fetch users (${res.status})`);
      err.status = res.status;
      throw err;
    }

    return res.json();
  }

  async createAdminUser(payloadOrUsername, password, role) {
    const payload = (typeof payloadOrUsername === 'object' && payloadOrUsername !== null)
      ? payloadOrUsername
      : { username: payloadOrUsername, password, role };
    const res = await fetch(`${API_BASE}/admin/users`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    const body = await res.json();
    if (!res.ok) {
      throw new Error(body.error || 'Failed to create user');
    }
    return body;
  }

  async toggleUserActive(username, isActive) {
    const res = await fetch(`${API_BASE}/admin/users/${username}/toggle-active`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ isActive })
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    const body = await res.json();
    if (!res.ok) {
      throw new Error(body.error || 'Failed to toggle user status');
    }
    return body;
  }

  async bulkUserAction(usernames, action) {
    const res = await fetch(`${API_BASE}/admin/users/bulk`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ usernames, action })
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    const body = await res.json();
    if (!res.ok) {
      throw new Error(body.error || 'Failed to perform bulk action');
    }
    return body;
  }

  async updateAdminUser(username, payloadOrNewUsername, password, role) {
    const payload = (typeof payloadOrNewUsername === 'object' && payloadOrNewUsername !== null)
      ? payloadOrNewUsername
      : { newUsername: payloadOrNewUsername, password, role };
    const res = await fetch(`${API_BASE}/admin/users/${username}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    const body = await res.json();
    if (!res.ok) {
      throw new Error(body.error || 'Failed to update user');
    }
    return body;
  }

  async deleteAdminUser(username, mode = 'soft') {
    const res = await fetch(`${API_BASE}/admin/users/${username}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ mode })
    });

    if (res.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }

    const body = await res.json();
    if (!res.ok) {
      throw new Error(body.error || 'Failed to delete user');
    }
    return body;
  }

  async clearDockerStorage() {
    const res = await fetch(`${API_BASE}/status/docker-storage/clear`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to clear Docker storage');
    }

    return res.json();
  }

  async clearDockerImages() {
    const res = await fetch(`${API_BASE}/status/docker-storage/clear-images`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) { throw new Error('Unauthorized'); }
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to clear Docker images'); }
    return res.json();
  }

  async clearDockerContainers() {
    const res = await fetch(`${API_BASE}/status/docker-storage/clear-containers`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) { throw new Error('Unauthorized'); }
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to clear Docker containers'); }
    return res.json();
  }

  async clearDockerBuildCache() {
    const res = await fetch(`${API_BASE}/status/docker-storage/clear-buildcache`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) { throw new Error('Unauthorized'); }
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to clear Docker build cache'); }
    return res.json();
  }

  async deleteDockerImage(imageRef) {
    const res = await fetch(`${API_BASE}/status/docker-images/${encodeURIComponent(imageRef)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) { throw new Error('Unauthorized'); }
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to delete Docker image'); }
    return res.json();
  }

  async generateApiKey() {
    const res = await fetch(`${API_BASE}/me/api-key`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to generate API Key');
    }

    return res.json();
  }

  async controlContainer(appName, action, options = {}) {
    this.ensureTokenLoaded();
    const ownerQuery = options.owner ? `?owner=${encodeURIComponent(options.owner)}` : '';
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/${action}${ownerQuery}`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      // Provide a user-friendly error message for unsupported operations
      if (res.status === 400 && body.error && body.error.includes('Docker/Compose')) {
        throw new Error(`Cannot ${action} this application: ${body.error}\n\nNote: Only Docker and Docker Compose applications can be started/stopped. Static frontends and other types run continuously.`);
      }
      throw new Error(body.error || `Failed to ${action} container`);
    }

    return res.json();
  }

  async getDeploymentStatus(deploymentId) {
    const res = await fetch(`${API_BASE}/deployments/${deploymentId}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to load deployment status');
    }

    return res.json();
  }

  async dismissDeployment(deploymentId) {
    const res = await fetch(`${API_BASE}/deployments/${deploymentId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to dismiss deployment');
    }

    return res.json();
  }

  async cancelDeployment(deploymentId) {
    if (this.activeUploads && this.activeUploads[deploymentId]) {
      const uploadReq = this.activeUploads[deploymentId].req;
      if (uploadReq && typeof uploadReq.abort === 'function') {
        uploadReq.abort();
      }
      delete this.activeUploads[deploymentId];
    }

    const res = await fetch(`${API_BASE}/deployments/${deploymentId}/cancel`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to cancel deployment');
    }

    return res.json();
  }

  async updateAppDomain(appName, domain, owner) {
    const body = { domain };
    if (owner) body.owner = owner;
    const res = await fetch(`${API_BASE}/apps/${appName}/domain`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body)
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    return res.json();
  }

  async analyzeZip(file, onProgress) {
    this.ensureTokenLoaded();
    const formData = new FormData();
    formData.append('file', file);
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/analyze-zip`);
      xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded * 100) / event.total);
            onProgress(percent);
          }
        };
      }
      
      xhr.onload = () => {
        if (xhr.status === 401) {
          return reject(new Error('Unauthorized'));
        }
        
        let response;
        try {
          response = JSON.parse(xhr.responseText);
        } catch (e) {
          return reject(new Error('Failed to parse server response'));
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(response);
        } else {
          reject(new Error(response.error || 'Failed to analyze ZIP'));
        }
      };
      
      xhr.onerror = () => reject(new Error('Network error occurred during ZIP upload'));
      xhr.send(formData);
    });
  }

  async scanAiProject(payload) {
    this.ensureTokenLoaded();
    
    let body, headers;
    if (payload.file) {
      body = new FormData();
      body.append('file', payload.file);
      if (payload.scanId) body.append('scanId', payload.scanId);
      headers = { 'Authorization': `Bearer ${this.token}` };
    } else {
      const jsonBody = { repoUrl: payload.repoUrl };
      if (payload.scanId) jsonBody.scanId = payload.scanId;
      body = JSON.stringify(jsonBody);
      headers = this.getAuthHeaders();
    }

    const res = await fetch(`${API_BASE}/ai-deploy/scan`, {
      method: 'POST',
      headers,
      body
    });
    
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to scan project');
    }
    return res.json();
  }

  async getAiScanStatus(scanId) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/ai-deploy/scan-status/${encodeURIComponent(scanId)}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) return { logs: [] };
    
    return res.json();
  }

  async deployAiProject(data) {
    this.ensureTokenLoaded();
    
    let body, headers;
    if (data.file) {
      body = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined) {
          body.append(key, data[key]);
        }
      });
      headers = { 'Authorization': `Bearer ${this.token}` };
    } else {
      body = JSON.stringify(data);
      headers = this.getAuthHeaders();
    }

    const res = await fetch(`${API_BASE}/ai-deploy/deploy`, {
      method: 'POST',
      headers,
      body
    });
    
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to deploy AI project');
    }
    return res.json();
  }

  async executeAiProject(data) {
    this.ensureTokenLoaded();
    
    let body, headers;
    if (data.file) {
      body = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined) {
          body.append(key, data[key]);
        }
      });
      headers = { 'Authorization': `Bearer ${this.token}` };
    } else {
      body = JSON.stringify(data);
      headers = this.getAuthHeaders();
    }

    const res = await fetch(`${API_BASE}/ai-deploy/execute`, {
      method: 'POST',
      headers,
      body
    });
    
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const resBody = await res.json().catch(() => ({}));
      throw new Error(resBody.error || 'Failed to execute deployment');
    }
    return res.json();
  }

  async downloadBackup() {
    const res = await fetch(`${API_BASE}/backups/download`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to download backup');
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.user.username}-deployments-${Date.now()}.zip`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return { success: true, message: 'Backup downloaded' };
  }

  async downloadAppSnapshot(appName, owner) {
    this.ensureTokenLoaded();
    const ownerQuery = owner ? `?owner=${encodeURIComponent(owner)}` : '';
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/snapshot/download${ownerQuery}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to download snapshot');
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const contentDisposition = res.headers.get('Content-Disposition');
    let fileName = `${appName}-snapshot.tar`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch.length > 1) {
        fileName = filenameMatch[1];
      }
    }
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return { success: true };
  }

  async getBackupInfo(file) {
    const formData = new FormData();
    formData.append('backup', file);

    const res = await fetch(`${API_BASE}/backups/info`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to read backup info');
    }

    return res.json();
  }

  async uploadBackup(file, confirm = false) {
    const formData = new FormData();
    formData.append('backup', file);
    if (confirm) formData.append('confirm', 'true');

    const res = await fetch(`${API_BASE}/backups/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to restore backup');
    }

    return res.json();
  }

  async listContainerFiles(appName, dir, owner) {
    const ownerQuery = owner ? `&owner=${encodeURIComponent(owner)}` : '';
    const dirQuery = dir ? `&dir=${encodeURIComponent(dir)}` : '';
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/files?_=1${dirQuery}${ownerQuery}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to list files');
    }

    const data = await res.json();
    const normalizedEntries = Array.isArray(data.entries)
      ? data.entries.map((entry) => {
          const isDir = typeof entry.isDir === 'boolean'
            ? entry.isDir
            : String(entry.type || '').toLowerCase() === 'directory';
          return { ...entry, isDir };
        })
      : [];

    return { ...data, entries: normalizedEntries };
  }

  async getFileContent(appName, filename, owner) {
    const ownerQuery = owner ? `&owner=${encodeURIComponent(owner)}` : '';
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/file?path=${encodeURIComponent(filename)}${ownerQuery}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch file content');
    }

    return res.json();
  }

  async updateFileContent(appName, filename, content, owner, restart = false) {
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/file`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ path: filename, content, owner, restart })
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to save file content');
    }

    return res.json();
  }

  async deleteContainerFile(appName, filePath, owner, restart = false) {
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/file`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ path: filePath, owner, restart })
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to delete file');
    }

    return res.json();
  }

  async createContainerDirectory(appName, dirPath, owner) {
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/directory`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ path: dirPath, owner })
    });
    if (res.status === 401) { throw new Error('Unauthorized'); }
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to create directory'); }
    return res.json();
  }

  async renameContainerItem(appName, oldPath, newPath, owner, restart = false) {
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/item/rename`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ oldPath, newPath, owner, restart })
    });
    if (res.status === 401) { throw new Error('Unauthorized'); }
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to rename item'); }
    return res.json();
  }

  async downloadContainerFile(appName, filePath, owner) {
    const ownerQuery = owner ? `&owner=${encodeURIComponent(owner)}` : '';
    const res = await fetch(`${API_BASE}/apps/${encodeURIComponent(appName)}/file/download?path=${encodeURIComponent(filePath)}${ownerQuery}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) { throw new Error('Unauthorized'); }
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to download file'); }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filePath.split('/').pop();
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async searchDockerHub(query, page = 1, pageSize = 25) {
    const res = await fetch(`${API_BASE}/docker-hub/search?q=${encodeURIComponent(query)}&page=${page}&page_size=${pageSize}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to search Docker Hub');
    }

    return res.json();
  }

  async getDockerHubTags(image, page = 1, pageSize = 25) {
    const res = await fetch(`${API_BASE}/docker-hub/tags?image=${encodeURIComponent(image)}&page=${page}&page_size=${pageSize}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch tags');
    }

    return res.json();
  }

  async inspectDockerHubImage(image, tag = 'latest') {
    const res = await fetch(`${API_BASE}/docker-hub/inspect?image=${encodeURIComponent(image)}&tag=${encodeURIComponent(tag)}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to inspect image');
    }

    return res.json();
  }

  async updateMyProfile({ newUsername, email, password, fullName }) {
    const res = await fetch(`${API_BASE}/me`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ newUsername, email, password, fullName })
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to update profile');
    }

    const data = await res.json();
    if (data.token) {
      this.setSession(data);
    }
    return data;
  }

  async createTerminalSession(appName, owner = null, commandOrOptions = '/bin/sh') {
    const query = owner ? `?owner=${encodeURIComponent(owner)}` : '';
    const body = typeof commandOrOptions === 'string'
      ? { command: commandOrOptions }
      : { ...(commandOrOptions || {}) };
    const res = await fetch(`${API_BASE}/terminal/${encodeURIComponent(appName)}/session${query}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body)
    });

    if (res.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || 'Failed to create terminal session');
      err.code = body.code || null;
      throw err;
    }

    return res.json();
  }



  uploadZipToContainer(appName, file, owner = null, dir = null, onProgress = null) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      const ownerQuery = owner ? `owner=${encodeURIComponent(owner)}` : '';
      const dirQuery = dir ? `dir=${encodeURIComponent(dir)}` : '';
      const qs = [ownerQuery, dirQuery].filter(Boolean).join('&');
      const url = `${API_BASE}/terminal/${encodeURIComponent(appName)}/upload-zip${qs ? `?${qs}` : ''}`;
      
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      
      const headers = this.getAuthHeaders();
      for (const [key, value] of Object.entries(headers)) {
        if (key !== 'Content-Type') {
          xhr.setRequestHeader(key, value);
        }
      }

      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status === 401) return reject(new Error('Unauthorized'));
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch(e) {
            resolve(xhr.responseText);
          }
        } else {
          try {
            const body = JSON.parse(xhr.responseText);
            reject(new Error(body.error || 'Failed to upload ZIP'));
          } catch(e) {
            reject(new Error('Failed to upload ZIP'));
          }
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(formData);
    });
  }

  getTerminalWsUrl(sessionId) {
    // Convert http(s) to ws(s)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    
    // If API_BASE is relative, use current host
    if (API_BASE.startsWith('/')) {
      return `${protocol}//${window.location.host}${API_BASE}/terminal/ws?sessionId=${sessionId}&token=${this.token}`;
    }
    
    // If API_BASE is absolute (e.g. http://localhost:3800/api), parse it
    try {
      const url = new URL(API_BASE);
      const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${wsProtocol}//${url.host}${url.pathname}/terminal/ws?sessionId=${sessionId}&token=${this.token}`;
    } catch (e) {
      // Fallback
      return `${protocol}//${window.location.host}/api/terminal/ws?sessionId=${sessionId}&token=${this.token}`;
    }
  }

  async terminalAiAssist(prompt, containerName = '', terminalHistory = '', owner = '') {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/terminal/ai-assist`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ prompt, containerName, terminalHistory, owner })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'AI assistant failed');
    }
    return res.json();
  }

  async fileAiAssist(prompt, filename = '', content = '', messages = []) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/terminal/file-ai-assist`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ prompt, filename, content, messages })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'File AI assistant failed');
    }
    return res.json();
  }

  async getWorkspaceChat(appName) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/terminal/${encodeURIComponent(appName)}/workspace-chat`, {
      headers: this.getAuthHeaders()
    });
    if (!res.ok) return { chat: [] };
    return res.json();
  }

  async saveWorkspaceChat(appName, chat) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/terminal/${encodeURIComponent(appName)}/workspace-chat`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ chat })
    });
    if (!res.ok) throw new Error('Failed to save chat');
    return res.json();
  }

  async workspaceAgent(appName, prompt, messages = [], { onEvent, isAuto, mode, workDir, signal } = {}) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/terminal/${encodeURIComponent(appName)}/workspace-agent`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        Accept: 'text/event-stream'
      },
      body: JSON.stringify({ prompt, messages, appName, stream: true, isAuto, mode, workDir }),
      signal
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Workspace AI agent failed');
    }

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('text/event-stream')) {
      return res.json();
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalPayload = null;
    let completion = null;

    const flushEvents = (chunk) => {
      buffer += chunk;
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';
      for (const part of parts) {
        const lines = part.split('\n');
        let event = 'message';
        const dataLines = [];
        for (const line of lines) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (!dataLines.length) continue;
        let data = {};
        try { data = JSON.parse(dataLines.join('\n')); } catch (_) { data = { raw: dataLines.join('\n') }; }
        if (typeof onEvent === 'function') onEvent(event, data);
        if (event === 'reply') {
          finalPayload = {
            success: true,
            reply: data.reply || '',
            tools: data.tools || [],
            workDir: data.workDir,
            containerName: data.containerName,
            verification: data.verification || null
          };
        } else if (event === 'done') {
          completion = data;
        } else if (event === 'error') {
          throw new Error(data.error || 'Workspace AI agent failed');
        }
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      flushEvents(decoder.decode(value, { stream: true }));
    }
    if (buffer.trim()) flushEvents('\n\n');
    if (!completion || !finalPayload) {
      throw new Error('Workspace AI stream ended before completion. Please retry.');
    }
    const verification = completion.verification || finalPayload.verification;
    return { ...finalPayload, verification, success: completion.success === true && (verification?.skipped === true || verification?.ok === true) };
  }

  async projectAiAssist(messages, tools = [], systemPrompt = '') {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/terminal/project-agent`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ messages, tools, systemPrompt })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Project AI assistant failed');
    }
    return res.json();
  }

  // ─── TICKETS API ──────────────────────────────────────────────────────────

  async getAdminTickets() {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/tickets/admin`, { headers: this.getAuthHeaders() });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch admin tickets');
    }
    return res.json();
  }

  async getUserTickets() {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/tickets`, { headers: this.getAuthHeaders() });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch your tickets');
    }
    return res.json();
  }

  async createTicket(subject, priority, description) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/tickets`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ subject, priority, description })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to create ticket');
    }
    return res.json();
  }

  async updateTicket(id, payload) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/tickets/admin/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to update ticket');
    }
    return res.json();
  }

  async deleteTicket(id) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/tickets/admin/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to delete ticket');
    }
    return res.json();
  }

  // Packages API
  async getPublicPackages() {
    const res = await fetch(`${API_BASE}/packages/public`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch public packages');
    }
    return res.json();
  }

  async getPackages({ signal } = {}) {
    this.ensureTokenLoaded();
    const res = await fetch(`${API_BASE}/packages`, {
      method: 'GET',
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch packages');
    }
    return res.json();
  }

  async updatePackages(packages) {
    this.ensureTokenLoaded();
    const headers = this.getAuthHeaders();
    headers['Content-Type'] = 'application/json';
    const res = await fetch(`${API_BASE}/packages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ packages })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to update packages');
    }
    return res.json();
  }

}

const apiService = new APIService();

// Global 401 response interceptor for all fetch requests
if (typeof window !== 'undefined' && !window.__devorants_fetch_intercepted__) {
  window.__devorants_fetch_intercepted__ = true;
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const res = await originalFetch.apply(this, args);
    if (res.status === 401) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      if (!url.includes('/login') && !url.includes('/verify-otp') && !url.includes('/forgot-password') && !url.includes('/verify-reset-code')) {
        try {
          res.clone().json().then(data => {
            apiService.handleUnauthorized(data);
          }).catch(() => {
            apiService.handleUnauthorized();
          });
        } catch (_) {
          apiService.handleUnauthorized();
        }
      }
    }
    return res;
  };
}

export { API_BASE };
export default apiService;
